import { ChangeDetectionStrategy, Component, Input, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import moment from 'moment';
import { debounceTime, map, tap } from 'rxjs/operators';
import { BehaviorSubject, merge, Observable, of } from 'rxjs';
import {
    DISPLAY_TIME_FORMATS,
    TimePrecision,
    MaskSettings,
    TooltipSettings,
    TimeintervalSettingsParts,
} from '@bazis/form/models/form-element.types';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SimpleData } from '@bazis/shared/models/srv.types';
import { DateValidators } from '@bazis/form/validators/dateValidators';
import { COMMON_DATE_MASK_SETTINGS, API_DATETIME_FORMAT } from '@bazis/configuration.service';
import { EntityFormControl } from '@bazis/form/models/form.types';
import { BazisFormService } from '@bazis/form/services/form.service';
import { ConditionalValidators } from '@bazis/form/validators/conditionalValidators';
import { BazisControlValueAccessor } from '@bazis/form/components/control-value-accessor.component';

@UntilDestroy()
@Component({
    selector: 'bazis-input-timeinterval',
    templateUrl: './input-timeinterval.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class BazisInputTimeintervalComponent extends BazisControlValueAccessor implements OnInit {
    @Input() titleKeyFrom: string = null;

    @Input() titleKeyTo: string = null;

    @Input() defaultInterval: boolean = true;

    // настройки отображения и работы тултипа
    @Input() tooltipSettings: TooltipSettings = null;

    @Input() inputFormat: string = API_DATETIME_FORMAT;

    @Input() precision: TimePrecision = 'minutes';

    @Input() showIntervalCheckbox = true;

    @Input() showDayAndNightCheckbox = true;

    @Input() fieldSettingsParts: TimeintervalSettingsParts = null;

    @Input() requiredFrom: boolean = null;

    @Input() requiredTo: boolean = null;

    disabled = false;

    formGroup = new FormGroup({
        from: new FormControl(null),
        to: new FormControl(null),
    });

    isDayAndNight = new FormControl([]);

    isInterval = new FormControl(['interval']);

    calendarValue: Date[];

    displayFormat: string;

    displayMaskFormat: string;

    displayIntervalMaskFormat: string;

    readonlyFormat: string;

    showTimePicker: 'from' | 'to' = null;

    mask = '';

    intervalMask = '';

    dayAndNightValue: { to: string; from: string };

    isIntervalOptions: SimpleData[] = [
        {
            id: 'interval',
            nameKey: 'title.range',
        },
    ];

    isDayAndNightOptions: SimpleData[] = [
        {
            id: 'dayAndNight',
            nameKey: 'title.dayAndNight',
        },
    ];

    needUpdateMaskSettings$ = new BehaviorSubject(null);

    maskSettings$: Observable<MaskSettings> = this.needUpdateMaskSettings$.pipe(
        map(() => ({
            mask: this.mask,
            ...COMMON_DATE_MASK_SETTINGS,
        })),
    );

    requiredFrom$: Observable<boolean>;

    requiredTo$: Observable<boolean>;

    ngOnInit() {
        if (this.ngControl.control instanceof EntityFormControl) {
            BazisFormService.getPropertiesFromConfig(this, this.ngControl.control.$config);
            this.ngControl.control.$config.componentValidatorFns = [this.validate.bind(this)];
        }
        this.ngControl.control.setValidators([this.validate.bind(this)]);

        this.displayFormat = DISPLAY_TIME_FORMATS[this.precision];
        this.displayMaskFormat = `${this.displayFormat}`;

        this.mask = this.displayMaskFormat.replace(/[A-Za-z]/g, '0');

        this.dayAndNightValue = this._calculateDayAndNightValue();

        this.formGroup.valueChanges
            .pipe(
                tap(() => {
                    this.setCalendarValues();
                    this.setControlValue();
                    if (
                        this.isDayAndNight.value.length > 0 &&
                        (this.formGroup.value.from !== this.dayAndNightValue.from ||
                            this.formGroup.value.to !== this.dayAndNightValue.to)
                    ) {
                        this.isDayAndNight.setValue([]);
                    } else if (
                        !this.isDayAndNight.value.length &&
                        this.formGroup.value.from === this.dayAndNightValue.from &&
                        this.formGroup.value.to === this.dayAndNightValue.to
                    ) {
                        this.isDayAndNight.setValue(['dayAndNight']);
                    }
                }),
                untilDestroyed(this),
            )
            .subscribe();

        merge(this.formGroup.statusChanges)
            .pipe(
                debounceTime(0),
                tap(() => {
                    if (this.formGroup.status !== 'INVALID') {
                        let errors = this.ngControl.control.errors;
                        if (!errors) return;
                        delete errors.componentError;
                        errors = Object.keys(errors).length > 0 ? errors : null;
                        this.ngControl.control.setErrors(errors);
                    } else {
                        this.ngControl.control.setErrors({
                            ...this.ngControl.control.errors,
                            componentError: { ...this.formGroup.errors },
                        });
                    }
                }),
                untilDestroyed(this),
            )
            .subscribe();

        this.isDayAndNight.valueChanges
            .pipe(
                tap(() => {
                    if (this.isDayAndNight.value && this.isDayAndNight.value.length > 0) {
                        if (!this.isInterval.value.length) this.isInterval.setValue(['interval']);

                        if (
                            this.formGroup.value.from !== this.dayAndNightValue.from ||
                            this.formGroup.value.to !== this.dayAndNightValue.to
                        )
                            this.formGroup.setValue(this.dayAndNightValue);

                        this.isInterval.disable();
                    } else {
                        this.isInterval.enable();
                    }
                }),
                untilDestroyed(this),
            )
            .subscribe();

        this.isInterval.valueChanges
            .pipe(
                tap(() => {
                    this.initValidators();
                    if (!this.isInterval.value || !this.isInterval.value.length) {
                        this.formGroup.get('to').setValue(null, { emitEvent: false });
                    }
                    this.formGroup.updateValueAndValidity();
                }),
                untilDestroyed(this),
            )
            .subscribe();

        this.initValidators();
        this.writeValue(this.ngControl.control.value);
        this.formGroup.updateValueAndValidity();
        this.ngControl.control.updateValueAndValidity();
    }

    onClickOutsideTimepicker(timepickerType) {
        if (this.showTimePicker === timepickerType) {
            this.showTimePicker = null;
        }
    }

    validate({ value }: FormControl) {
        return this.formGroup.status === 'VALID' ? null : { componentError: true };
    }

    initValidators() {
        const validators = [];
        const fromValidators = [DateValidators.invalidTimeValidatorFn()];
        const toValidators = [DateValidators.invalidTimeValidatorFn()];
        if (this.ngControl.control instanceof EntityFormControl) {
            const entityControl: EntityFormControl = this.ngControl.control as EntityFormControl;
            if (this.fieldSettingsParts) {
                const fromSettings =
                    entityControl.$config.fieldConfig[this.fieldSettingsParts.fromTimeFieldName];

                this.requiredFrom$ = fromSettings.required?.$;

                fromValidators.push(ConditionalValidators.required(() => fromSettings.required?._));

                if (this.fieldSettingsParts.toTimeFieldName) {
                    const toSettings =
                        entityControl.$config.fieldConfig[this.fieldSettingsParts.toTimeFieldName];
                    this.requiredTo$ = toSettings.required?.$;
                    toValidators.push(ConditionalValidators.required(() => toSettings.required?._));
                }
            }
        } else {
            if (this.requiredFrom) {
                fromValidators.push(Validators.required);
                this.requiredFrom$ = of(this.requiredFrom);
            }
            if (this.requiredTo) {
                toValidators.push(Validators.required);
                this.requiredTo$ = of(this.requiredTo);
            }
        }

        if (this.isInterval.value.length > 0) {
            validators.push(DateValidators.intervalSeparateTimeValidatorFn('from', 'to'));
        }

        this.formGroup.get('from').setValidators(fromValidators);
        this.formGroup.get('to').setValidators(toValidators);
        this.formGroup.setValidators(validators);
    }

    getMomentTime(part: string) {
        const momentTime = moment(part, this.displayFormat, true);
        return momentTime && momentTime.isValid()
            ? moment()
                  .hours(momentTime.hours())
                  .minutes(momentTime.minutes())
                  .seconds(momentTime.seconds())
                  .milliseconds(momentTime.milliseconds())
            : null;
    }

    getMomentValues() {
        const value = this.formGroup.value;

        return {
            from: value.from ? this.getMomentTime(value.from) : null,
            to: value.to ? this.getMomentTime(value.to) : null,
            fromPart: value.from,
            toPart: value.to,
        };
    }

    setCalendarValues() {
        const momentValues = this.getMomentValues();
        this.calendarValue = [
            momentValues.from ? momentValues.from.toDate() : null,
            momentValues.to ? momentValues.to.toDate() : null,
        ];
    }

    setControlValue() {
        const momentValues = this.getMomentValues();
        let value = [null, null];
        if (momentValues.from || momentValues.fromPart) {
            value[0] = momentValues.from ? momentValues.from.format(this.inputFormat) : null; // null = invalid
        }
        if (momentValues.to || momentValues.toPart) {
            value[1] = momentValues.to ? momentValues.to.format(this.inputFormat) : null; // null = invalid
        }

        if (!value[0] && !value[1]) {
            value = [];
        }
        if (this.fieldSettingsParts) {
            const newValue = {
                [this.fieldSettingsParts.fromTimeFieldName]:
                    value.length > 0 && value[0]
                        ? moment(value[0], this.inputFormat).format(
                              this.fieldSettingsParts.timeFormat,
                          )
                        : null,
            };
            if (this.fieldSettingsParts.toTimeFieldName) {
                newValue[this.fieldSettingsParts.toTimeFieldName] =
                    value.length === 2 && value[1]
                        ? moment(value[1], this.inputFormat).format(
                              this.fieldSettingsParts.timeFormat,
                          )
                        : null;
            }
            if (this.ngControl.control.enabled) {
                this.onChange(newValue);
            }
            return;
        }

        this.onChange(value);
    }

    public setDisabledState(isDisabled: boolean): void {
        if (isDisabled) {
            this.formGroup.disable();
            this.isInterval.disable();
            this.isDayAndNight.disable();
        } else {
            this.formGroup.enable();
            this.isDayAndNight.enable();
            if (!this.isDayAndNight.value) {
                this.isInterval.enable();
            }
        }
    }

    public writeValue(value): void {
        // transform from parts to default timeinterval values
        if (!this.ngControl.control) return;
        if (this.fieldSettingsParts) {
            this.inputFormat = 'YYYY-MM-DD ' + this.fieldSettingsParts.timeFormat;
            if (value) {
                const fromName = this.fieldSettingsParts.fromTimeFieldName;
                const from = value[fromName] ? '1970-01-01 ' + value[fromName] : null;
                const toName = this.fieldSettingsParts.toTimeFieldName;

                if (!toName) {
                    value = [from];
                } else {
                    const to = value[toName] ? '1970-01-01 ' + value[toName] : null;
                    value = [from, to];
                }
            }
        }

        if (!this.defaultInterval && (!value || value.length <= 1 || !value[1])) {
            this.isInterval.setValue([]);
        }

        if (!value || value.length === 0) {
            this.formGroup.setValue({ from: null, to: null });
            this.setCalendarValues();
            return;
        }
        const from = value[0];
        const to = value[1];

        const displayFromValue = moment(from, this.inputFormat);
        const displayToValue = moment(to, this.inputFormat);

        let formattedFromValue =
            from && displayFromValue?.isValid()
                ? displayFromValue.format(this.displayFormat)
                : null;

        let formattedToValue =
            to && displayToValue?.isValid() ? displayToValue.format(this.displayFormat) : null;

        if (
            this.formGroup.value.from !== formattedFromValue ||
            this.formGroup.value.to !== formattedToValue
        ) {
            this.formGroup.setValue({ from: formattedFromValue, to: formattedToValue });
        }

        if (formattedToValue) {
            this.isInterval.setValue(['interval']);
        }
        this.setCalendarValues();
    }

    setValueFromTimeCalendar(value: string, timepickerType) {
        this.formGroup.patchValue({ [timepickerType]: value });
    }

    openTimeCalendar(timepickerType) {
        this.showTimePicker = this.showTimePicker === timepickerType ? null : timepickerType;
    }

    private _calculateDayAndNightValue() {
        const endOfTheDay = moment()
            .hours(23)
            .minutes(59)
            .seconds(59)
            .milliseconds(999)
            .format(this.displayFormat);
        const startOfTheDay = moment()
            .hours(0)
            .minutes(0)
            .seconds(0)
            .milliseconds(0)
            .format(this.displayFormat);
        return { from: startOfTheDay, to: endOfTheDay };
    }
}
